<template>
    <FormKit
        type="group"
        :name="id"
    >
        <div :id="id" class="flex scroll-mt-4 flex-col gap-4">
            <div class="flex flex-col gap-2">
                <p class="text-lg font-semibold leading-none">
                    {{ title }}
                    <span v-if="isMandatory" class="font-bold text-red-500">*</span>
                </p>
                <p class="flex justify-between">
                    <span>{{ getUserExplanationExtraItemsWrapper() }}</span>
                    <i>{{ $t('shopPage.product.selected') }}: {{ productForm().totalSelectedItemsForExtra(id as string) }} <span v-if="hasMaximumSelectionQuantity">{{ $t('shopPage.product.of') }} {{ Math.abs(maximumSelectionQuantity as number) }}</span></i>
                </p>
            </div>
            <!-- TODO: fix TS issue after this is done https://github.com/formkit/formkit/issues/1450 -->
            <transition name="fade" mode="out-in">
                <FormKitMessages
                    :node="itemsRef?.node"
                    :library="FormKitMessagesCustomComponents"
                    :sections-schema="
                        // @ts-expect-error
                        {
                            message: {
                                $el: undefined,
                                $cmp: 'Notification',
                                props: {
                                    type: 'error'
                                },
                                children: '$message.value'
                            }
                        }"
                />
            </transition>
            <div class="grid w-full grid-cols-1 gap-4" :class="{ 'auto-cols-auto grid-cols-2 md:grid-cols-3': viewType === ViewTypeOrderBuddy.GRID }">
                <FormKit
                    :id="id"
                    ref="itemsRef"
                    type="group"
                    name="items"
                    validation="+validateTotalExtraItemsWrapper"
                    :validation-rules="{ validateTotalExtraItemsWrapper }"
                    :validation-messages="{
                        validateTotalExtraItemsWrapper: getValidationMessageExtraItemsWrapper()
                    }"
                    validation-visibility="blur">
                    <template
                        v-for="item in items"
                        :key="item!.id">
                        <component
                            :is="viewType === ViewTypeOrderBuddy.GRID ? ExtraGridItem : ExtraDefaultItem"
                            :extra-id="id"
                            v-bind="item"
                            :show-prices="showPrices"
                            :maximum-selection-quantity="maximumSelectionQuantity"
                            :minimum-selection-quantity="minimumSelectionQuantity">
                            <FormKit
                                v-if="(maximumSelectionQuantity && maximumSelectionQuantity > 1) || (minimumSelectionQuantity && minimumSelectionQuantity > 1) || item?.maximumSelectionQuantity && item.maximumSelectionQuantity > 1"
                                v-model="productForm().currentProductWithSelectedExtras.extras[id].items[item!.id].quantity"
                                type="stepNumber"
                                :small="true"
                                :disabled="productForm().extraItemIsDisabled(id, item!.id, maximumSelectionQuantity)"
                                :max="productForm().extraItemMaxQuantity(id, item!.id, maximumSelectionQuantity, item.maximumSelectionQuantity)"
                                :min="0"
                                name="quantity"
                                :classes="{
                                    outer: {
                                        'pr-4': viewType !== ViewTypeOrderBuddy.GRID, // TODO: try to move this to the ProductExtrasDefaultItem component (detail, not important)
                                        'relative': viewType === ViewTypeOrderBuddy.GRID,
                                        'z-40': viewType === ViewTypeOrderBuddy.GRID
                                    }
                                }"
                                @input="productForm().extraItemQuantityLogic($event, id, item!.id)"
                            />
                        </component>
                    </template>
                </FormKit>
            </div>
        </div>
    </FormKit>
</template>

<script setup lang="ts">
    import type { ExtrasProps } from '~/types/local-types'
    import { FormKitMessages } from '@formkit/vue'
    import ExtraDefaultItem from '~/components/Extra/ExtraDefaultItem.vue'
    import ExtraGridItem from '~/components/Extra/ExtraGridItem.vue'
    import Notification from '~/components/Notification/Notification.vue'
    import { productForm } from '~/store/productForm'
    import { ViewTypeOrderBuddy } from '~/types/orderbuddy-types'
    const props = defineProps<ExtrasProps>()
    const { t } = useI18n()
    const itemsRef = ref()
    const hasMinimumSelectionQuantity = (props.minimumSelectionQuantity !== null && props.minimumSelectionQuantity > 0)
    const hasMaximumSelectionQuantity = (props.maximumSelectionQuantity !== null && props.maximumSelectionQuantity > 0)
    const hasNoMinimumOrMaximum = !hasMinimumSelectionQuantity && !hasMaximumSelectionQuantity
    const hasMinimumSelectionQuantityOnly = hasMinimumSelectionQuantity && !hasMaximumSelectionQuantity
    const hasMaximumSelectionQuantityOnly = !hasMinimumSelectionQuantity && hasMaximumSelectionQuantity
    const hasEqualMinimumAndMaximumSelectionQuantity = hasMinimumSelectionQuantity && hasMaximumSelectionQuantity && props.maximumSelectionQuantity === props.minimumSelectionQuantity
    const hasUnequalMinimumAndMaximumSelectionQuantity = hasMinimumSelectionQuantity && hasMaximumSelectionQuantity && props.maximumSelectionQuantity !== props.minimumSelectionQuantity
    const isMandatory = hasMinimumSelectionQuantityOnly || hasEqualMinimumAndMaximumSelectionQuantity || hasUnequalMinimumAndMaximumSelectionQuantity
    const FormKitMessagesCustomComponents = { Notification: markRaw(Notification) }
    function validateTotalExtraItemsWrapper (node: any) {
        // This only works realtime if you use the values of the node, not the values of the store :(
        const totalValues = Object.values(node.value).reduce((total: number, extraItem: any) => {
            return total + (extraItem.selected ? (extraItem.quantity || 1) : 0)
        }, 0)
        if (hasNoMinimumOrMaximum) {
            return true // No minimum or maximum e.g. 0 0  or null null
        } else if (hasMinimumSelectionQuantityOnly) {
            return totalValues >= props.minimumSelectionQuantity // Minimum only e.g. 1 0 or 1 null
        } else if (hasMaximumSelectionQuantityOnly) {
            return true // Maximum only e.g. 0 1 or null 1
        } else if (hasEqualMinimumAndMaximumSelectionQuantity) {
            return totalValues === props.minimumSelectionQuantity
        } else if (hasUnequalMinimumAndMaximumSelectionQuantity) {
            return totalValues >= props.minimumSelectionQuantity && totalValues <= props.maximumSelectionQuantity // Minimum and maximum not equal e.g. 1 2
        } else {
            return true
        }
    }
    function getUserExplanationExtraItemsWrapper () {
        if (hasMinimumSelectionQuantityOnly) {
            return `${t('shopPage.product.selectAtLeast')} ${props.minimumSelectionQuantity} ${props.minimumSelectionQuantity > 1 ? t('shopPage.product.tems') : t('shopPage.product.item')}.`
        } else if (hasMaximumSelectionQuantityOnly) {
            return `${t('shopPage.product.selectAMaximumOf')} ${props.maximumSelectionQuantity} ${props.maximumSelectionQuantity > 1 ? t('shopPage.product.items') : t('shopPage.product.item')}.`
        } else if (hasEqualMinimumAndMaximumSelectionQuantity) {
            return `${t('shopPage.product.selectExactly')} ${props.maximumSelectionQuantity} ${t('shopPage.product.items')}.`
        } else if (hasUnequalMinimumAndMaximumSelectionQuantity) {
            return `${t('shopPage.product.selectBetween')} ${props.minimumSelectionQuantity} ${t('shopPage.product.and')} ${props.maximumSelectionQuantity} ${t('shopPage.product.items')}.`
        } else {
            return t('shopPage.product.whatItemsWouldYouLike')
        }
    }
    function getValidationMessageExtraItemsWrapper () {
        if (hasMinimumSelectionQuantityOnly) {
            const remaining = props.minimumSelectionQuantity - productForm().totalSelectedItemsForExtra(props.id as string)
            return `${t('shopPage.product.select')} ${remaining} ${t('shopPage.product.more')} ${remaining > 1 ? t('shopPage.product.items') : t('shopPage.product.item')}.`
        } else if (hasEqualMinimumAndMaximumSelectionQuantity) {
            const remaining = props.maximumSelectionQuantity - productForm().totalSelectedItemsForExtra(props.id as string)
            return `${t('shopPage.product.select')} ${remaining} ${t('shopPage.product.more')} ${remaining > 1 ? t('shopPage.product.items') : t('shopPage.product.item')}.`
        } else if (hasUnequalMinimumAndMaximumSelectionQuantity) {
            const remaining = props.minimumSelectionQuantity - productForm().totalSelectedItemsForExtra(props.id as string)
            return `${t('shopPage.product.select')} ${remaining} ${t('shopPage.product.more')} ${remaining > 1 ? t('shopPage.product.items') : t('shopPage.product.item')}.`
        } else {
            return ''
        }
    }
</script>
